import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageSquarePlus, Loader2, CheckCircle2, X, Paperclip } from "lucide-react";
import { toast } from "sonner";
import { feedbackApi, apiError } from "@/lib/api";

const CATEGORIES = [
  { value: "nie_dziala", label: "Coś nie działa" },
  { value: "wynik_ai", label: "Problem z wynikiem AI" },
  { value: "kredyty_platnosc", label: "Kredyty lub płatność" },
  { value: "konto", label: "Problem z kontem" },
  { value: "sugestia", label: "Sugestia" },
  { value: "inne", label: "Inne" },
];

const MAX_BYTES = 5 * 1024 * 1024;
const OK_TYPES = ["image/png", "image/jpeg", "image/webp"];

function moduleFromPath(p) {
  if (p.startsWith("/cv")) return "CV";
  if (p.startsWith("/biznes/generator")) return "Generator biznes";
  if (p.startsWith("/biznes/analiz")) return "Analiza biznes";
  if (p.startsWith("/biznes")) return "Biznes";
  if (p.includes("kredyt") || p.includes("platnosc") || p.includes("bonus")) return "Kredyty i płatności";
  if (p.startsWith("/biblioteka") || p.startsWith("/pakiet")) return "Biblioteka";
  if (p.startsWith("/konto")) return "Konto";
  if (p.startsWith("/admin")) return "Panel admina";
  if (p.startsWith("/asystent")) return "Asystent";
  return "Ogólne";
}

function captureRefs(search) {
  const sp = new URLSearchParams(search);
  const refs = {};
  if (sp.get("sid")) refs.project_id = sp.get("sid");
  if (sp.get("purchase_id")) refs.tx_id = sp.get("purchase_id");
  if (sp.get("op") || sp.get("op_id")) refs.op_id = sp.get("op") || sp.get("op_id");
  return refs;
}

function captureTech() {
  try {
    return {
      user_agent: navigator.userAgent,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      platform: navigator.platform,
      language: navigator.language,
    };
  } catch {
    return {};
  }
}

export function FeedbackWidget() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [category, setCategory] = useState("nie_dziala");
  const [description, setDescription] = useState("");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [file, setFile] = useState(null);
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    const openHandler = () => { setSent(false); setOpen(true); };
    window.addEventListener("open-feedback", openHandler);
    return () => window.removeEventListener("open-feedback", openHandler);
  }, []);

  const reset = () => {
    setCategory("nie_dziala"); setDescription(""); setEmail("");
    setConsent(false); setFile(null); setError("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const close = () => { setOpen(false); setTimeout(() => { setSent(false); reset(); }, 200); };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) { setFile(null); return; }
    if (!OK_TYPES.includes(f.type)) { setError("Dozwolone formaty zrzutu: PNG, JPG, WEBP."); e.target.value = ""; return; }
    if (f.size > MAX_BYTES) { setError("Zrzut jest za duży (maks. 5 MB)."); e.target.value = ""; return; }
    setError(""); setFile(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!description.trim()) { setError("Opis jest wymagany."); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("category", category);
      fd.append("description", description.trim());
      if (email.trim()) fd.append("contact_email", email.trim());
      fd.append("consent_contact", consent ? "true" : "false");
      fd.append("url", location.pathname + location.search);
      fd.append("module", moduleFromPath(location.pathname));
      fd.append("refs", JSON.stringify(captureRefs(location.search)));
      fd.append("tech", JSON.stringify(captureTech()));
      if (file) fd.append("file", file);
      const res = await feedbackApi.submit(fd);
      setSent(true);
      toast.success(res.message || "Dziękujemy! Zgłoszenie zostało wysłane.");
      reset();
    } catch (err) {
      setError(apiError(err.response?.data?.detail, "Nie udało się wysłać zgłoszenia. Spróbuj ponownie."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        type="button"
        data-testid="feedback-fab"
        onClick={() => { setSent(false); setOpen(true); }}
        aria-label="Zgłoś problem lub uwagę"
        className="fixed bottom-5 right-5 z-[60] inline-flex items-center gap-2 rounded-full border border-brand-line bg-brand-surface px-4 py-3 text-[13px] font-semibold text-brand-text shadow-lg transition-transform duration-200 hover:-translate-y-0.5 hover:border-brand-accent/60"
      >
        <MessageSquarePlus size={18} strokeWidth={2} />
        <span className="hidden sm:inline">Zgłoś problem lub uwagę</span>
      </button>

      {open && (
        <div className="fixed inset-0 z-[70] flex items-end justify-center bg-black/60 p-0 backdrop-blur-sm sm:items-center sm:p-4" data-testid="feedback-dialog" onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}>
          <div className="w-full max-w-lg overflow-hidden rounded-t-panel border border-brand-line bg-brand-surface sm:rounded-panel">
            <div className="flex items-center justify-between border-b border-brand-line px-5 py-4">
              <h2 className="font-display text-lg font-semibold text-brand-text">Zgłoś problem lub uwagę</h2>
              <button type="button" data-testid="feedback-close" onClick={close} className="text-brand-muted hover:text-brand-text"><X size={20} /></button>
            </div>

            {sent ? (
              <div className="px-5 py-10 text-center" data-testid="feedback-success">
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-brand-accent/15 text-brand-accent"><CheckCircle2 size={26} /></div>
                <p className="font-display text-lg font-semibold text-brand-text">Dziękujemy za zgłoszenie!</p>
                <p className="mt-1 text-[14px] text-brand-sec">Zapisaliśmy je i zajmiemy się nim najszybciej jak to możliwe.</p>
                <button type="button" onClick={close} data-testid="feedback-done" className="mt-5 rounded-button bg-brand-accent px-5 py-2.5 text-sm font-semibold text-brand-on">Zamknij</button>
              </div>
            ) : (
              <form onSubmit={submit} className="max-h-[75vh] space-y-4 overflow-y-auto px-5 py-5">
                {error && <div data-testid="feedback-error" className="rounded-field border border-red-500/40 bg-red-500/10 px-3 py-2 text-[13px] text-red-300">{error}</div>}

                <div>
                  <label className="mb-1.5 block text-sm font-medium text-brand-text">Kategoria</label>
                  <select data-testid="feedback-category" value={category} onChange={(e) => setCategory(e.target.value)} className="w-full rounded-field border border-brand-line bg-brand-bg px-3 py-2.5 text-[15px] text-brand-text outline-none focus:border-brand-accent">
                    {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-medium text-brand-text">Opis <span className="text-brand-accent">*</span></label>
                  <textarea data-testid="feedback-description" rows={4} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Opisz, co się stało lub co chcesz zasugerować…" className="w-full rounded-field border border-brand-line bg-brand-bg px-3 py-2.5 text-[15px] text-brand-text outline-none focus:border-brand-accent" maxLength={5000} />
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-medium text-brand-text">E-mail kontaktowy <span className="text-brand-muted">(opcjonalnie)</span></label>
                  <input data-testid="feedback-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="ty@firma.pl" className="w-full rounded-field border border-brand-line bg-brand-bg px-3 py-2.5 text-[15px] text-brand-text outline-none focus:border-brand-accent" />
                </div>

                <div>
                  <input ref={fileRef} data-testid="feedback-file" type="file" accept="image/png,image/jpeg,image/webp" onChange={onFile} className="hidden" id="feedback-file-input" />
                  <label htmlFor="feedback-file-input" className="inline-flex cursor-pointer items-center gap-2 rounded-field border border-brand-line bg-brand-bg px-3 py-2 text-[13px] text-brand-sec hover:text-brand-text">
                    <Paperclip size={15} /> {file ? file.name : "Dodaj zrzut ekranu (opcjonalnie)"}
                  </label>
                </div>

                <label className="flex cursor-pointer items-start gap-2.5 text-[13px] text-brand-sec">
                  <input data-testid="feedback-consent" type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 h-4 w-4 accent-brand-accent" />
                  <span>Zgadzam się na kontakt zwrotny w sprawie tego zgłoszenia.</span>
                </label>

                <button type="submit" disabled={loading} data-testid="feedback-submit" className="flex w-full items-center justify-center gap-2 rounded-button bg-brand-accent px-5 py-3 text-sm font-semibold text-brand-on disabled:opacity-60">
                  {loading ? <><Loader2 size={16} className="animate-spin" /> Wysyłanie…</> : "Wyślij zgłoszenie"}
                </button>
                <p className="text-center text-[11px] text-brand-muted">Nie zbieramy haseł ani danych karty. Dołączamy tylko adres strony i podstawowe dane techniczne do diagnozy.</p>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
